import { useRef } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { Alert, ModalButtonWithForm } from 'components/common';
import { FormGroup, Text } from 'components/common/form';

import type { FormApi } from 'informed';
import type { GetCompetenciesRes } from 'shared-types/responses';

type FormState = Record<'name', string>;

interface Props {
  competency: GetCompetenciesRes['competencyObjs'][0];
  updateCompetencies: () => Promise<void>;
}

const CompetencyCopy = ({ competency, updateCompetencies }: Props) => {
  const { dispatch } = useDispatch();
  const { messages } = useReduxState();
  const formApi = useRef<FormApi<FormState> | undefined>(undefined);
  const router = useRouter();

  const handleSubmit = async (values: FormState) => {
    try {
      const response = await api.post(
        `/admin/competencies/${competency.id}/copy`,
        {
          name: values.name
        }
      );

      if (response.data?.success) {
        dispatch.addNotice({
          text: messages.competenciesCopySuccess,
          type: 'success'
        });

        // Update the competencies list
        await updateCompetencies();

        // Navigate to the new competency
        router.push(`/users/competencies/${response.data.competencyId}`);
      } else {
        throw new Error('Something went wrong...');
      }
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  const validateName = async (value: string) => {
    if (!value?.trim()) return messages.commonTitleEmpty;

    try {
      await api.head(`/admin/competencies?name=${value}`);
      return messages.commonTitleInUse;
    } catch (err) {
      return undefined;
    }
  };

  return (
    <ModalButtonWithForm
      buttonIcon="copy"
      buttonIsDropdown={true}
      buttonText={messages.competenciesCopy}
      modalActionText={messages.commonConfirm}
      modalTitle={messages.competenciesCopy}
      onSubmit={handleSubmit}
      pristineIsValid={false}
      setFormApi={(api: FormApi<FormState>) => (formApi.current = api)}
    >
      {() => (
        <>
          <Alert
            style={{ marginBottom: 10 }}
            text={messages.competenciesCopyInfo}
          />
          <FormGroup label={messages.commonTitle}>
            <Text
              field="name"
              id="name"
              required={true}
              customValidateAsync={validateName}
              validateOnBlur={true}
              validateOnChange={true}
            />
          </FormGroup>
        </>
      )}
    </ModalButtonWithForm>
  );
};

export default CompetencyCopy;
